import React, { createContext, useContext, useState, useEffect } from 'react';

const WeatherContext = createContext();

export const useWeather = () => {
  const context = useContext(WeatherContext);
  if (!context) {
    throw new Error('useWeather must be used within a WeatherProvider');
  }
  return context;
};

export const WeatherProvider = ({ children }) => {
  const API_KEY = 'd8a49a713cf63f56643c40eaf75aa3bb';
  const [location, setLocation] = useState('Miami,FL');
  const [weather, setWeather] = useState({
    location: 'Miami, FL',
    currentWeather: {
      temperature: '28.5',
      condition: 'Clear',
      humidity: 65,
      timestamp: new Date().toISOString()
    },
    forecast: [
      {
        date: new Date().toISOString(),
        temperature: '28.5',
        condition: 'Clear',
        humidity: 65
      },
      // Default forecast data
      ...Array(6).fill(null).map((_, i) => ({
        date: new Date(Date.now() + (i+1) * 86400000).toISOString(),
        temperature: (Math.random() * 5 + 25).toFixed(1),
        condition: ['Clear', 'Partly Cloudy', 'Cloudy', 'Light Rain'][Math.floor(Math.random() * 4)],
        humidity: Math.floor(Math.random() * 20 + 60)
      }))
    ],
    loading: false,
    error: null,
    geoLocationStatus: null
  });

  // Fetch weather data on component mount
  useEffect(() => {
    fetchWeatherData();
  }, []);

  const fetchWeatherData = async () => {
    try {
      setWeather(prev => ({ ...prev, loading: true, error: null }));
      
      // Fetch current weather
      const currentResponse = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?q=${location}&units=metric&appid=${API_KEY}`
      );
      
      if (!currentResponse.ok) {
        throw new Error('Failed to fetch current weather');
      }
      
      const currentData = await currentResponse.json();
      
      // Fetch forecast
      const forecastResponse = await fetch(
        `https://api.openweathermap.org/data/2.5/forecast?q=${location}&units=metric&appid=${API_KEY}`
      );
      
      if (!forecastResponse.ok) {
        throw new Error('Failed to fetch forecast');
      }
      
      const forecastData = await forecastResponse.json();
      
      // Process forecast data to get daily forecasts
      const dailyForecasts = processForecastData(forecastData);
      
      setWeather({
        location: `${currentData.name}, ${currentData.sys.country}`,
        currentWeather: {
          temperature: currentData.main.temp.toFixed(1),
          condition: currentData.weather[0].main,
          humidity: currentData.main.humidity,
          timestamp: new Date().toISOString()
        },
        forecast: dailyForecasts,
        loading: false,
        error: null,
        geoLocationStatus: weather.geoLocationStatus
      });
    } catch (error) {
      console.error('Error fetching weather data:', error);
      setWeather(prev => ({ 
        ...prev, 
        loading: false, 
        error: 'Unable to fetch weather data. Using sample data instead.',
      }));
    }
  };

  const fetchWeatherByCoordinates = async (lat, lon) => {
    try {
      setWeather(prev => ({ ...prev, loading: true, error: null }));
      
      // Fetch current weather by coordinates
      const currentResponse = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&units=metric&appid=${API_KEY}`
      );
      
      if (!currentResponse.ok) {
        throw new Error('Failed to fetch current weather');
      }
      
      const currentData = await currentResponse.json();
      
      // Fetch forecast by coordinates
      const forecastResponse = await fetch(
        `https://api.openweathermap.org/data/2.5/forecast?lat=${lat}&lon=${lon}&units=metric&appid=${API_KEY}`
      );
      
      if (!forecastResponse.ok) {
        throw new Error('Failed to fetch forecast');
      }
      
      const forecastData = await forecastResponse.json();
      
      // Process forecast data to get daily forecasts
      const dailyForecasts = processForecastData(forecastData);
      
      // Update the location state with the fetched location name
      setLocation(`${currentData.name}, ${currentData.sys.country}`);
      
      setWeather({
        location: `${currentData.name}, ${currentData.sys.country}`,
        currentWeather: {
          temperature: currentData.main.temp.toFixed(1),
          condition: currentData.weather[0].main,
          humidity: currentData.main.humidity,
          timestamp: new Date().toISOString()
        },
        forecast: dailyForecasts,
        loading: false,
        error: null,
        geoLocationStatus: 'success'
      });
    } catch (error) {
      console.error('Error fetching weather data by coordinates:', error);
      setWeather(prev => ({ 
        ...prev, 
        loading: false, 
        error: 'Unable to fetch weather for your location. Check permissions or try a manual search.',
        geoLocationStatus: 'error'
      }));
    }
  };

  // Process the forecast data to get one forecast per day
  const processForecastData = (forecastData) => {
    const dailyForecasts = [];
    const dailyMap = {};
    
    forecastData.list.forEach(item => {
      const date = new Date(item.dt * 1000);
      const day = date.toDateString();
      
      if (!dailyMap[day] && dailyForecasts.length < 7) {
        dailyMap[day] = true;
        dailyForecasts.push({
          date: date.toISOString(),
          temperature: item.main.temp.toFixed(1),
          condition: item.weather[0].main,
          humidity: item.main.humidity
        });
      }
    });
    
    return dailyForecasts;
  };

  const refreshWeather = () => {
    fetchWeatherData();
  };

  const updateLocation = (newLocation) => {
    setLocation(newLocation);
    fetchWeatherData();
  };

  const getCurrentLocation = () => {
    setWeather(prev => ({ ...prev, geoLocationStatus: 'loading' }));
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          fetchWeatherByCoordinates(latitude, longitude);
        },
        (error) => {
          console.error("Geolocation error:", error);
          setWeather(prev => ({ 
            ...prev, 
            geoLocationStatus: 'error',
            error: `Geolocation error: ${error.message}. Please try manual search.`
          }));
        },
        { timeout: 10000 }
      );
    } else {
      setWeather(prev => ({ 
        ...prev, 
        geoLocationStatus: 'unsupported',
        error: 'Geolocation is not supported by your browser. Please try manual search.'
      }));
    }
  };

  const value = {
    weather,
    refreshWeather,
    updateLocation,
    getCurrentLocation
  };

  return (
    <WeatherContext.Provider value={value}>
      {children}
    </WeatherContext.Provider>
  );
};